<script setup lang="ts">
import type { FundingDisplayDescriptor } from '~/utils/event-policy';
import { formatTimeRemaining } from '~/composables/format-duration';
import { formatCurrency } from '~/composables/format-currency';
import { formatDeadline } from '~/composables/format-datetime';

const props = defineProps<{
  funding: FundingDisplayDescriptor;
}>();

// Absolute deadline in the event timezone; only shown when both are known.
const deadlineText = computed(() =>
  props.funding.fundraisingDeadline && props.funding.timezone
    ? formatDeadline(props.funding.fundraisingDeadline, props.funding.timezone)
    : null,
);

const countdownText = computed(() =>
  formatTimeRemaining(props.funding.timeRemaining),
);
</script>

<template>
  <div v-if="funding.mode !== 'hidden'" class="event-funding-module">
    <!-- Fundraising: progress bar + deadline -->
    <template v-if="funding.mode === 'fundraising'">
      <v-progress-linear
        :model-value="Math.min(funding.progressPercentage, 100)"
        :color="funding.isGoalMet ? 'success' : 'primary'"
        rounded
        height="8"
        class="mb-2"
      />
      <div class="d-flex justify-space-between text-body-2 mb-1">
        <span>
          <template v-if="funding.isGoalMet && funding.committedCount !== null">
            <v-icon size="small" color="success" class="mr-1"
              >mdi-check-circle</v-icon
            >
            <template v-if="funding.thresholdType === 'cash'">
              Goal reached &mdash; {{ formatCurrency(funding.committedCount) }}
              raised
            </template>
            <template v-else-if="funding.thresholdType === 'attendees'">
              Goal reached &mdash; {{ funding.committedCount }}
              {{ funding.committedCount === 1 ? 'attendee' : 'attendees' }}
              committed
            </template>
            <template v-else>
              Goal reached &mdash; {{ funding.committedCount }}
              {{ funding.committedCount === 1 ? 'backer' : 'backers' }}
              committed
            </template>
          </template>
          <template v-else-if="funding.committedCount !== null">
            <template v-if="funding.thresholdType === 'cash'">
              {{ formatCurrency(funding.committedCount) }} of
              {{ formatCurrency(funding.threshold) }} raised
            </template>
            <template v-else-if="funding.thresholdType === 'attendees'">
              {{ funding.committedCount }} of {{ funding.threshold }}
              {{ funding.threshold === 1 ? 'attendee' : 'attendees' }}
            </template>
            <template v-else>
              {{ funding.committedCount }} of {{ funding.threshold }}
              {{ funding.threshold === 1 ? 'backer' : 'backers' }}
            </template>
          </template>
          <template v-else-if="funding.isGoalMet">
            <v-icon size="small" color="success" class="mr-1"
              >mdi-check-circle</v-icon
            >
            Goal reached
          </template>
          <template v-else> {{ funding.progressPercentage }}% funded </template>
        </span>
        <span v-if="funding.isSoldOut" class="text-error font-weight-medium">
          Sold out
        </span>
      </div>
      <!--
        Wraps only between whole pieces (mirrors EventCard): when both don't
        fit on one line the countdown drops to its own left-aligned line. The
        separator rides on the end of the deadline piece, glued to the date,
        so it never starts a line.
      -->
      <div
        v-if="deadlineText || countdownText"
        class="d-flex flex-wrap gc-1 text-body-2 text-medium-emphasis"
      >
        <span
          v-if="deadlineText"
          class="funding-deadline"
          data-testid="funding-deadline"
        >
          Fundraising ends
          <span class="funding-deadline__date"
            ><strong>{{ deadlineText }}</strong
            ><template v-if="countdownText">&nbsp;&middot;</template></span
          >
        </span>
        <span
          v-if="countdownText"
          class="text-no-wrap"
          data-testid="funding-countdown"
        >
          {{ countdownText }}
        </span>
      </div>
    </template>

    <!-- Finalization: indeterminate bar + processing message -->
    <template v-else-if="funding.mode === 'finalization'">
      <v-progress-linear
        indeterminate
        color="secondary"
        rounded
        height="8"
        class="mb-2"
      />
      <div class="text-body-2 font-weight-medium mb-1">
        Processing payments...
      </div>
      <div
        v-if="funding.finalizationCount !== null"
        class="text-body-2 text-medium-emphasis"
      >
        {{ funding.finalizationCount }} of
        {{ funding.committedCount ?? funding.threshold }}
        processed
      </div>
    </template>

    <!-- Post-confirmation: checkmark + status -->
    <template v-else-if="funding.mode === 'post_confirmation'">
      <div class="d-flex align-center text-body-2">
        <v-icon color="success" size="small" class="mr-2"
          >mdi-check-circle</v-icon
        >
        <span>
          <template v-if="funding.paidCount !== null">
            <template v-if="funding.thresholdType === 'cash'">
              {{ formatCurrency(funding.paidCount) }} in ticket sales
            </template>
            <template v-else-if="funding.thresholdType === 'attendees'">
              {{ funding.paidCount }}
              {{ funding.paidCount === 1 ? 'attendee' : 'attendees' }} confirmed
            </template>
            <template v-else> {{ funding.paidCount }} tickets sold </template>
          </template>
          <template v-else> Event confirmed </template>
        </span>
      </div>
    </template>

    <!-- Failed -->
    <template v-else-if="funding.mode === 'failed'">
      <v-alert type="error" variant="tonal" density="compact">
        {{ funding.message }}
      </v-alert>
    </template>

    <!-- Canceled -->
    <template v-else-if="funding.mode === 'canceled'">
      <v-alert type="error" variant="tonal" density="compact">
        {{ funding.message }}
      </v-alert>
    </template>

    <!-- Pre-fundraising -->
    <template v-else-if="funding.mode === 'pre_fundraising'">
      <div class="text-body-2 text-medium-emphasis">{{ funding.message }}</div>
    </template>
  </div>
</template>

<style scoped>
.event-funding-module {
  /* Lets the deadline date relax its no-wrap in a very narrow column. */
  container-type: inline-size;
}

.funding-deadline {
  /* Flex items default to min-width: auto; allow shrinking to the card. */
  min-width: 0;
}

/* The date/time (and its trailing separator) stays on one line... */
.funding-deadline__date {
  white-space: nowrap;
}

/* ...unless the column is too narrow to hold it, where wrapping beats overflow. */
@container (max-width: 240px) {
  .funding-deadline__date {
    white-space: normal;
  }
}
</style>
